import { db, auth } from '../../../../../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  increment,
  writeBatch,
} from 'firebase/firestore';

export const PROGRESS_STATUSES = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
};

// Toggle like for a project
export const toggleProjectLike = async (projectId, userId) => {
  try {
    if (!projectId || !userId) throw new Error('Missing projectId or userId');
    const likeRef = doc(db, 'projectLikes', `${projectId}_${userId}`);
    const projectRef = doc(db, 'projects', projectId);
    const likeDoc = await getDoc(likeRef);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) throw new Error('Project not found');

    const isLiked = likeDoc.exists();
    const current = projectDoc.data()?.likeCount || 0;

    const batch = writeBatch(db);
    if (isLiked) {
      batch.delete(likeRef);
      batch.update(projectRef, { likeCount: Math.max(0, current - 1) });
    } else {
      batch.set(likeRef, { projectId, userId, timestamp: serverTimestamp() });
      batch.update(projectRef, { likeCount: current + 1 });
    }
    await batch.commit();

    const updated = await getDoc(projectRef);
    const likeCount = updated.data()?.likeCount || 0;
    return { isLiked: !isLiked, likes: likeCount };
  } catch (e) {
    console.error('Error toggling project like:', e);
    throw e;
  }
};

// Toggle follow for a project
export const toggleProjectFollow = async (projectId, userId) => {
  try {
    if (!projectId || !userId) throw new Error('Missing projectId or userId');
    const followRef = doc(db, 'projectFollows', `${projectId}_${userId}`);
    const projectRef = doc(db, 'projects', projectId);
    const followDoc = await getDoc(followRef);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) throw new Error('Project not found');

    const isFollowed = followDoc.exists();
    const current = projectDoc.data()?.followerCount || 0;
    const batch = writeBatch(db);
    if (isFollowed) {
      batch.delete(followRef);
      batch.update(projectRef, { followerCount: Math.max(0, current - 1) });
    } else {
      batch.set(followRef, { projectId, userId, timestamp: serverTimestamp() });
      batch.update(projectRef, { followerCount: current + 1 });
    }
    await batch.commit();

    const updated = await getDoc(projectRef);
    const followerCount = updated.data()?.followerCount || 0;
    return { isFollowed: !isFollowed, followers: followerCount };
  } catch (e) {
    console.error('Error toggling project follow:', e);
    throw e;
  }
};

// Increment share count for a project
export const incrementProjectShare = async (projectId) => {
  try {
    if (!projectId) throw new Error('Missing projectId');
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, { shareCount: increment(1) });
    const updated = await getDoc(projectRef);
    const shareCount = updated.data()?.shareCount || 0;
    return { shares: shareCount };
  } catch (e) {
    console.error('Error incrementing project share:', e);
    throw e;
  }
};

// Increment updates count for a project
export const incrementProjectUpdates = async (projectId) => {
  try {
    if (!projectId) throw new Error('Missing projectId');
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, { updatesCount: increment(1), updatedAt: serverTimestamp() });
    const updated = await getDoc(projectRef);
    const updatesCount = updated.data()?.updatesCount || 0;
    return { updates: updatesCount };
  } catch (e) {
    console.error('Error incrementing project updates:', e);
    throw e;
  }
};

// Update arbitrary project fields (creator-only per rules)
export const updateProjectFields = async (projectId, updates = {}) => {
  try {
    if (!auth?.currentUser) throw new Error('No authenticated user');
    if (!projectId) throw new Error('Missing projectId');
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, { ...updates, updatedAt: serverTimestamp() });
    const snap = await getDoc(projectRef);
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.error('Error updating project fields:', e);
    throw e;
  }
};

export const updateProjectStatus = async (projectId, status, options = {}) => {
  try {
    const result = await updateProjectFields(projectId, { status });

    // If project is being launched (status -> 'active'), notify team members
    if (status === 'active' && !options.skipNotification) {
      try {
        const projectRef = doc(db, 'projects', projectId);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          const teamMemberIds = projectData.teamMemberIds || [];
          const creatorId = projectData.creatorId || auth.currentUser?.uid;

          // Import notification service dynamically to avoid circular deps
          const { default: notificationService } = await import('../../../../../features/notifications/services/notificationService');

          // Notify all team members (except the one who launched)
          const membersToNotify = teamMemberIds.filter(id => id !== auth.currentUser?.uid);

          for (const memberId of membersToNotify) {
            try {
              await notificationService.createNotification({
                userId: memberId,
                actorId: creatorId,
                type: 'project_launched',
                projectId: projectId,
                projectTitle: projectData.title?.substring(0, 100)
              });
            } catch (notifyErr) {
              console.warn('Failed to notify member about project launch:', notifyErr);
            }
          }
        }
      } catch (notifyError) {
        console.warn('Failed to send project launch notifications:', notifyError);
        // Don't throw - launch succeeded, notifications are secondary
      }
    }

    return result;
  } catch (e) {
    console.error('Error updating project status:', e);
    throw e;
  }
};

export const setPhaseProgress = async (projectId, userId, phaseId, status) => {
  if (!projectId || !userId || !phaseId) throw new Error('Missing ids for setPhaseProgress');
  const ref = doc(db, 'project_progress', `${projectId}_${userId}`);
  await setDoc(
    ref,
    {
      projectId,
      userId,
      phases: { [phaseId]: status },
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

export const setTaskProgress = async (projectId, userId, phaseId, taskId, status) => {
  if (!projectId || !userId || !phaseId || !taskId) throw new Error('Missing ids for setTaskProgress');
  const ref = doc(db, 'project_progress', `${projectId}_${userId}`);
  const key = `${phaseId}:${taskId}`;
  await setDoc(
    ref,
    {
      projectId,
      userId,
      tasks: { [key]: status },
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

// Toggle or set feedback (boolean) for a task for a specific user
export const setTaskFeedback = async (projectId, userId, phaseId, taskId, value) => {
  if (!projectId || !userId || !phaseId || !taskId) throw new Error('Missing ids for setTaskFeedback');
  const ref = doc(db, 'project_progress', `${projectId}_${userId}`);
  const key = `${phaseId}:${taskId}`;
  await setDoc(
    ref,
    {
      projectId,
      userId,
      feedback: { [key]: !!value },
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

// Set a per-user date for a task (stored as ISO string in project_progress.dates)
export const setTaskDate = async (projectId, userId, phaseId, taskId, iso) => {
  if (!projectId || !userId || !phaseId || !taskId) throw new Error('Missing ids for setTaskDate');
  const ref = doc(db, 'project_progress', `${projectId}_${userId}`);
  const key = `${phaseId}:${taskId}`;
  await setDoc(
    ref,
    {
      projectId,
      userId,
      dates: { [key]: iso || null },
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

// Add a comment to a task
export const addTaskCommentToFirebase = async (projectId, phaseId, taskId, comment) => {
  try {
    if (!auth?.currentUser) throw new Error('No authenticated user');
    if (!projectId || !phaseId || !taskId || !comment) throw new Error('Missing required fields');

    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);

    if (!projectSnap.exists()) throw new Error('Project not found');

    const projectData = projectSnap.data();
    const phases = projectData.phases || [];
    const phaseIndex = phases.findIndex(p => p.id === phaseId);

    if (phaseIndex === -1) throw new Error('Phase not found');

    const tasks = phases[phaseIndex].tasks || [];
    const taskIndex = tasks.findIndex(t => t.id === taskId);

    if (taskIndex === -1) throw new Error('Task not found');

    // Create updated phases array
    const updatedPhases = [...phases];
    const updatedTasks = [...tasks];
    const task = { ...updatedTasks[taskIndex] };

    // Add comment
    const comments = task.comments || [];
    const newComment = {
      ...comment,
      createdAt: serverTimestamp(), // Use server timestamp for consistency
      updatedAt: serverTimestamp()
    };

    task.comments = [...comments, newComment];
    updatedTasks[taskIndex] = task;
    updatedPhases[phaseIndex] = { ...updatedPhases[phaseIndex], tasks: updatedTasks };

    // Update project document
    await updateDoc(projectRef, {
      phases: updatedPhases,
      updatedAt: serverTimestamp(),
      updatesCount: increment(1)
    });

    // Send notifications
    // 1. Notify Creator (if not the commenter)
    if (projectData.creatorId && projectData.creatorId !== auth.currentUser.uid) {
      const { createNotification } = await import('../../../../auth/utils/firebase/projectCollab');
      await createNotification({
        userId: projectData.creatorId,
        type: 'task_comment',
        actorId: auth.currentUser.uid,
        projectId,
        message: `commented on task "${task.title}"`,
      });
    }

    // 2. Notify Assignees (if not the commenter)
    if (task.assignedTo && task.assignedTo.length > 0) {
      const { createNotification } = await import('../../../../auth/utils/firebase/projectCollab');
      for (const assigneeId of task.assignedTo) {
        if (assigneeId !== auth.currentUser.uid && assigneeId !== projectData.creatorId) {
          await createNotification({
            userId: assigneeId,
            type: 'task_comment',
            actorId: auth.currentUser.uid,
            projectId,
            message: `commented on task "${task.title}"`,
          });
        }
      }
    }

    return newComment;
  } catch (error) {
    console.error('Error adding task comment:', error);
    throw error;
  }
};

// Toggle like on a task comment
export const toggleTaskCommentLikeInFirebase = async (projectId, phaseId, taskId, commentId, userId) => {
  try {
    if (!projectId || !phaseId || !taskId || !commentId || !userId) throw new Error('Missing required fields');

    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);

    if (!projectSnap.exists()) throw new Error('Project not found');

    const projectData = projectSnap.data();
    const phases = projectData.phases || [];
    const phaseIndex = phases.findIndex(p => p.id === phaseId);

    if (phaseIndex === -1) throw new Error('Phase not found');

    const tasks = phases[phaseIndex].tasks || [];
    const taskIndex = tasks.findIndex(t => t.id === taskId);

    if (taskIndex === -1) throw new Error('Task not found');

    const task = tasks[taskIndex];
    const comments = task.comments || [];
    const commentIndex = comments.findIndex(c => c.id === commentId);

    if (commentIndex === -1) throw new Error('Comment not found');

    // Create updated structure
    const updatedPhases = [...phases];
    const updatedTasks = [...tasks];
    const updatedComments = [...comments];
    const comment = { ...updatedComments[commentIndex] };

    // Toggle like
    const likes = comment.likes || [];
    const likeIndex = likes.indexOf(userId);

    if (likeIndex === -1) {
      comment.likes = [...likes, userId];
    } else {
      comment.likes = likes.filter(id => id !== userId);
    }

    updatedComments[commentIndex] = comment;
    updatedTasks[taskIndex] = { ...task, comments: updatedComments };
    updatedPhases[phaseIndex] = { ...updatedPhases[phaseIndex], tasks: updatedTasks };

    // Update project document
    await updateDoc(projectRef, {
      phases: updatedPhases
    });

    return {
      commentId,
      likes: comment.likes,
      isLiked: likeIndex === -1
    };
  } catch (error) {
    console.error('Error toggling comment like:', error);
    throw error;
  }
};

/**
 * Update a task in a project
 * @param {string} projectId - Project ID
 * @param {string} phaseId - Phase ID  
 * @param {string} taskId - Task ID
 * @param {Object} updates - Fields to update (title, content, etc.)
 * @returns {Promise<Object>} Updated task
 */
export const updateTaskInFirebase = async (projectId, phaseId, taskId, updates) => {
  try {
    if (!auth?.currentUser) throw new Error('No authenticated user');
    if (!projectId || !phaseId || !taskId) throw new Error('Missing required IDs');

    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);

    if (!projectSnap.exists()) throw new Error('Project not found');

    const projectData = projectSnap.data();
    const phases = projectData.phases || [];
    const phaseIndex = phases.findIndex(p => p.id === phaseId);

    if (phaseIndex === -1) throw new Error('Phase not found');

    const tasks = phases[phaseIndex].tasks || [];
    const taskIndex = tasks.findIndex(t => t.id === taskId);

    if (taskIndex === -1) throw new Error('Task not found');

    const task = tasks[taskIndex];

    // Permission check: user must be task creator or project creator
    const isTaskCreator = task.createdBy === auth.currentUser.uid;
    const isProjectCreator = projectData.creatorId === auth.currentUser.uid;

    if (!isTaskCreator && !isProjectCreator) {
      throw new Error('You do not have permission to edit this task');
    }

    // Create updated phases array
    const updatedPhases = [...phases];
    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = {
      ...task,
      ...updates,
      updatedAt: serverTimestamp()
    };
    updatedPhases[phaseIndex] = { ...updatedPhases[phaseIndex], tasks: updatedTasks };

    // Update project document
    await updateDoc(projectRef, {
      phases: updatedPhases,
      updatedAt: serverTimestamp()
    });

    return {
      ...task,
      ...updates,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};
